import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  InputAdornment,
  Typography,
  Paper
} from '@mui/material';
import {
  Search,
  Clear,
  FilterList,
  CalendarToday,
  Person,
  Business,
  Payment
} from '@mui/icons-material';
import axios from '../../config/axios';

const ReservationFilters = ({ reservations, onFiltersChange }) => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    paymentStatus: '',
    reservableId: '',
    userId: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const [reservables, setReservables] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchReservables();
    fetchUsers();
  }, []);

  const fetchReservables = async () => {
    try {
      const response = await axios.get('/api/reservables');
      setReservables(response.data);
    } catch (err) {
      console.error('Error fetching reservables:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/user');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      search: '',
      status: '',
      paymentStatus: '',
      reservableId: '',
      userId: '',
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
      completed: 'Completata'
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      paid: 'Pagata',
      refunded: 'Rimborsata',
      failed: 'Fallita'
    };
    return labels[status] || status;
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3, 
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <FilterList color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Filtri di Ricerca
          </Typography>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={`${getActiveFiltersCount()} attivo${getActiveFiltersCount() !== 1 ? 'i' : ''}`}
              color="primary"
              size="small"
              sx={{ 
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                fontWeight: 600
              }}
            />
          )}
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Clear />}
          onClick={clearFilters}
          disabled={getActiveFiltersCount() === 0}
          sx={{ 
            minWidth: 140,
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.light',
              color: 'primary.dark',
            },
            '&:disabled': {
              borderColor: 'grey.300',
              color: 'grey.400',
            }
          }}
        >
          Cancella filtri
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Search Field */}
        <TextField
          fullWidth
          label="Cerca per nome, cognome, risorsa o note"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          variant="outlined"
          size="large"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              fontSize: '1.1rem',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              },
            },
          }}
        />

        {/* Filter Controls */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Status Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel sx={{ color: 'text.secondary' }}>Stato Prenotazione</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Stato Prenotazione"
              size="large"
              sx={{
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Tutti gli stati</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="pending">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="In attesa" color="warning" size="small" />
                </Box>
              </MenuItem>
              <MenuItem value="approved">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Approvata" color="success" size="small" />
                </Box>
              </MenuItem>
              <MenuItem value="rejected">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Rifiutata" color="error" size="small" />
                </Box>
              </MenuItem>
              <MenuItem value="cancelled">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Annullata" color="default" size="small" />
                </Box>
              </MenuItem>
              <MenuItem value="completed">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Completata" color="primary" size="small" />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Payment Status Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel sx={{ color: 'text.secondary' }}>Stato Pagamento</InputLabel>
            <Select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              label="Stato Pagamento"
              size="large"
              sx={{
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment color="action" />
                  <Typography variant="body2" color="text.secondary">Tutti gli stati</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="pending">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment color="action" />
                  <Typography variant="body2">In attesa</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="paid">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment color="action" />
                  <Typography variant="body2">Pagata</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="refunded">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment color="action" />
                  <Typography variant="body2">Rimborsata</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="failed">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment color="action" />
                  <Typography variant="body2">Fallita</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Reservable Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel sx={{ color: 'text.secondary' }}>Risorsa</InputLabel>
            <Select
              value={filters.reservableId}
              onChange={(e) => handleFilterChange('reservableId', e.target.value)}
              label="Risorsa"
              size="large"
              sx={{
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business color="action" />
                  <Typography variant="body2" color="text.secondary">Tutte le risorse</Typography>
                </Box>
              </MenuItem>
              {reservables.map((reservable) => (
                <MenuItem key={reservable._id} value={reservable._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business color="action" />
                    <Typography variant="body2">{reservable.name} ({reservable.category})</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* User Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel sx={{ color: 'text.secondary' }}>Utente</InputLabel>
            <Select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              label="Utente"
              size="large"
              sx={{
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="action" />
                  <Typography variant="body2" color="text.secondary">Tutti gli utenti</Typography>
                </Box>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="action" />
                    <Typography variant="body2">{user.name} {user.surname} ({user.email})</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Date Range Filters */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <TextField
            // sx={{ minWidth: 200, flex: 1 }}
            label="Da data"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="large"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarToday color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              },
            }}
          />

          <TextField
            // sx={{ minWidth: 200, flex: 1 }}
            label="A data"
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="large"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarToday color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Active Filters Display */}
      {getActiveFiltersCount() > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Filtri attivi:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filters.search && (
              <Chip
                label={`Ricerca: "${filters.search}"`}
                onDelete={() => handleFilterChange('search', '')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {filters.status && (
              <Chip
                label={`Stato: ${getStatusLabel(filters.status)}`}
                onDelete={() => handleFilterChange('status', '')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {filters.paymentStatus && (
              <Chip
                label={`Pagamento: ${getPaymentStatusLabel(filters.paymentStatus)}`}
                onDelete={() => handleFilterChange('paymentStatus', '')}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
            {filters.reservableId && (
              <Chip
                label={`Risorsa: ${reservables.find(r => r._id === filters.reservableId)?.name || 'N/A'}`}
                onDelete={() => handleFilterChange('reservableId', '')}
                color="warning"
                variant="outlined"
                size="small"
              />
            )}
            {filters.userId && (
              <Chip
                label={`Utente: ${users.find(u => u._id === filters.userId)?.name || 'N/A'} ${users.find(u => u._id === filters.userId)?.surname || ''}`}
                onDelete={() => handleFilterChange('userId', '')}
                color="success"
                variant="outlined"
                size="small"
              />
            )}
            {filters.dateFrom && (
              <Chip
                label={`Da: ${filters.dateFrom}`}
                onDelete={() => handleFilterChange('dateFrom', '')}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
            {filters.dateTo && (
              <Chip
                label={`A: ${filters.dateTo}`}
                onDelete={() => handleFilterChange('dateTo', '')}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ReservationFilters;
